// Adapted from: https://github.com/JamesRandall/react-azure-adb2c/blob/master/src/react-azure-adb2c.js
import * as Msal from 'msal';
import { setUserInfo } from '../actions/userdata-actions';
import { fetchAppServiceCenters, initializeNotifications } from '../actions/utility-actions';
import { AppUser, SessionRead } from '../models/utilities/app-user';
import { getApplicationStore } from '../store/configure-store';
import { Api } from './api';
import { ApiEndpoints } from './app-constants';
import { AppSettings } from './app-settings';

const msalLogger = new Msal.Logger(loggerCallback, { level: Msal.LogLevel.Error });

const state = {
    stopLoopingRedirect : false,
    launchApp           : () => {return; },
    accessToken         : '',
    scopes              : []
};

function loggerCallback(logLevel: Msal.LogLevel, message: string, piiLoggingEnabled: boolean) {
    console.log(message);
}

function authCallback(error: Msal.AuthError, response: Msal.AuthResponse) {

    const localMsalApp = window.msal as Msal.UserAgentApplication;

    // Reset Password Flow ... we don't support yet...
    if (error && error.errorMessage.indexOf('AADB2C90118') > -1) {

        localMsalApp.authority = `${ AppSettings._adInstanceUrl }/${ AppSettings._adTenant }/${ AppSettings._adResetPolicy }`;        
        acquireToken();
    
    }
    // User does not have an existing session for some reason
    else if (error && error.errorMessage.indexOf('AADB2C90077') > -1) {

        const authParams = {
            scopes: [AppSettings._adScope]
        } as Msal.AuthenticationParameters;

        localMsalApp.loginRedirect(authParams);
    
    }
    else if (error) {
    
        console.log(error + ':' + error);
        state.stopLoopingRedirect = true;

        const lowered = error.errorMessage.toLowerCase();
        if (lowered.indexOf('invalid state') > -1) {
            location.reload();
        }

    } else {
    
        acquireToken();
    
    }

}

/**
 * Ask the API what the signed-in user may do. Without an answer the user holds nothing, and every page says so.
 */
async function loadSession(): Promise<SessionRead | undefined> {
    try {
        return await Api.UntypedGet(ApiEndpoints.ACCOUNT_SESSION);
    }
    catch (err) {
        console.error(err);
        return undefined;
    }
}

function acquireTokenSilent(user: any) {

    const authParams = {
        scopes: [AppSettings._adScope]
    } as Msal.AuthenticationParameters;

    const localMsalApp = window.msal as Msal.UserAgentApplication;
    localMsalApp.acquireTokenSilent(authParams).then((response : Msal.AuthResponse) => {

        state.accessToken = response.accessToken;

        if (state && state.launchApp) {
            
            const claims = new AppUser(user.idToken);
            const store = getApplicationStore() as any;

            loadSession().then((session) => {

                // Store User Info, with what the API says the user may do
                store.dispatch(setUserInfo(AppUser.withSession(claims, session)));

                // Set default Service Center
                store.dispatch(fetchAppServiceCenters()); 

                // Prime the user's notification panel
                store.dispatch(initializeNotifications());

                // Start the app
                state.launchApp();
            });

        } 

    }, (error) => {
        if (error) {
            console.error(error);
        }

        localMsalApp.acquireTokenRedirect(authParams);
    });
}

function acquireToken() {

    const localMsalApp = window.msal as Msal.UserAgentApplication;
    const account = localMsalApp.getAccount();
    if (!account) {

        const authParams = {
            scopes: [AppSettings._adScope]
        } as Msal.AuthenticationParameters;

        localMsalApp.loginRedirect(authParams);
    }
    else {
        acquireTokenSilent(account);
    }

}

const AuthenticationManager = {

    initialize: () => {

        const config = {
            auth: {
                clientId                : AppSettings._adClient,
                authority               : `${AppSettings._adInstanceUrl}${AppSettings._adTenant}/${AppSettings._adPolicy}`,
                redirectUri             : AppSettings._clientUrl,
                validateAuthority       : false,
                postLogoutRedirectUri   : AppSettings._postLogoutUrl
            },
            cache: {
                cacheLocation           : 'localStorage',
                storeAuthStateInCookie  : false
            },
            system: {
                logger                  : msalLogger,
                loadFrameTimeout        : 15000
            }
        } as Msal.Configuration;

        const msalObj = new Msal.UserAgentApplication(config);
        msalObj.handleRedirectCallback(authCallback);

    },

    run: (launchApp: any) => {

        state.launchApp = launchApp;
        if (!(window.msal as Msal.UserAgentApplication).isCallback(window.location.hash) && window.parent === window && !window.opener) {
            if (!state.stopLoopingRedirect) {
                acquireToken();
            }
        }

    },

    signOut: () => {
        localStorage.removeItem('state.userdata');

         // Clear Local storage since MSAL doesn't
         const storage = window.localStorage;
         if (storage) {
             let key: string;
             for (key in storage) {
                 if (storage.hasOwnProperty(key)) {
                     if (key.indexOf("msal") !== -1) {
                         window.localStorage.removeItem(key);
                     }
                 }
             }
         }
 
        (window.msal as Msal.UserAgentApplication).logout();
    },

    getUser: () => (window.msal as Msal.UserAgentApplication).getAccount()
    
};

export { AuthenticationManager };
